import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { z } from "zod";
import {
  IMPORT_ENTITIES,
  IMPORT_ENTITY_LABEL,
  importModeAllowed,
  importTemplateHeaders,
  type ImportEntity,
  type ImportMode,
  type ImportRowsResult,
  type ImportView,
} from "@cx/contracts";
import { can, type Module, type Permissions } from "@cx/db";
import { toCsv } from "@cx/domain";
import { ConfirmDialog, ContentHeader, QueryStatus, StatusBadge, useToast, type Tone } from "@cx/ui";
import { downloadText } from "../../download";
import { errorMessage } from "../../errors";
import { dateTime } from "../../format";
import { useSession } from "../../session";

export const importsSearch = z.object({
  tipo: z.enum(IMPORT_ENTITIES).optional().catch(undefined),
  id: z
    .string()
    .regex(/^[0-9A-HJKMNP-TV-Z]{26}$/)
    .optional()
    .catch(undefined),
});

const MODULE: Record<ImportEntity, Module> = {
  products: "products",
  contacts: "contacts",
  receivables: "receivables",
  payables: "payables",
};
const STATUS: Record<ImportView["status"], readonly [Tone, string]> = {
  validating: ["info", "Validando"],
  validated: ["warning", "Aguardando confirmação"],
  committing: ["info", "Gravando"],
  done: ["success", "Concluída"],
  failed: ["danger", "Falhou"],
  canceled: ["secondary", "Cancelada"],
};
const ROW_STATUS: Record<string, readonly [Tone, string]> = {
  error: ["danger", "Com erro"],
  valid: ["success", "Válida"],
  done: ["success", "Gravada"],
  failed: ["danger", "Não gravada"],
};
const ROW_FILTERS = [
  ["error", "Com erro"],
  ["valid", "Válidas"],
  ["all", "Todas"],
] as const;
const busy = (s: ImportView["status"]) => s === "validating" || s === "committing";
const n = (x: number) => x.toLocaleString("pt-BR");

/** Prévia validada que passou das 24 h: não confirma mais. */
export const importExpired = (v: ImportView, now = Date.now()) =>
  v.status === "validated" && Date.parse(v.expiresAt) <= now;
const expired = (v: ImportView) => importExpired(v);
/** Rótulo da situação, com "Prévia vencida" no lugar de "Aguardando confirmação". */
export const importStatusLabel = (v: ImportView, now = Date.now()) =>
  importExpired(v, now) ? "Prévia vencida" : STATUS[v.status][1];
/**
 * O que a tela oferece: confirmar (prévia válida), retomar (gravação interrompida) e cancelar (em andamento, ou a
 * gravação interrompida, para encerrar sem retomar).
 */
export function importActions(v: ImportView, now = Date.now()) {
  const c = v.counts;
  const resume =
    v.status === "failed" && v.committedAt !== null && c.created + c.updated + c.failed < c.valid;
  return {
    canCommit: v.status === "validated" && !importExpired(v, now) && c.valid > 0,
    resume,
    canCancel: resume || v.status === "validating" || v.status === "validated" || v.status === "committing",
  };
}
/** Modelo em CSV (";" e BOM, como as exportações): só o cabeçalho, com o custo só para quem pode vê-lo. */
export const importTemplateCsv = (entity: ImportEntity, withCost: boolean) =>
  toCsv([importTemplateHeaders(entity, { withCost })]);

/** Preferências › Importação de planilhas (T1.15): envio, prévia com erros por linha, confirmação e relatórios. */
export function ImportsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const allowed = IMPORT_ENTITIES.filter((e) => can(perms, MODULE[e], "create"));
  const search = useSearch({ from: "/preferencias/importacoes" });
  const nav = useNavigate({ from: "/preferencias/importacoes" });
  const entity: ImportEntity | undefined =
    search.tipo && allowed.includes(search.tipo) ? search.tipo : allowed[0];

  if (!entity)
    return (
      <>
        <ContentHeader
          title="Importação de planilhas"
          breadcrumb={["Preferências", "Importação de planilhas"]}
        />
        <div className="box card p-4 text-muted">Você não tem permissão para importar cadastros.</div>
      </>
    );
  return (
    <>
      <ContentHeader
        title="Importação de planilhas"
        breadcrumb={["Preferências", "Importação de planilhas"]}
      />
      <ul className="nav nav-tabs mb-3" role="tablist">
        {allowed.map((e) => (
          <li className="nav-item" role="presentation" key={e}>
            <button
              type="button"
              role="tab"
              aria-selected={e === entity}
              className={`nav-link${e === entity ? " active" : ""}`}
              onClick={() => nav({ search: { tipo: e } })}
            >
              {IMPORT_ENTITY_LABEL[e]}
            </button>
          </li>
        ))}
      </ul>
      <UploadCard
        key={`${companyId}-${entity}`}
        entity={entity}
        canUpdate={can(perms, MODULE[entity], "update")}
        withCost={can(perms, "products", "read_cost")}
        onCreated={(id) => nav({ search: { tipo: entity, id } })}
      />
      {search.id && (
        <ImportDetail key={search.id} entity={entity} id={search.id} api={api} companyId={companyId} />
      )}
      <ImportsList
        entity={entity}
        selected={search.id}
        onOpen={(id) => nav({ search: { tipo: entity, id } })}
      />
    </>
  );
}

function UploadCard(p: {
  entity: ImportEntity;
  canUpdate: boolean;
  withCost: boolean;
  onCreated(id: string): void;
}): ReactElement {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const upsertOk = importModeAllowed(p.entity, "upsert") && p.canUpdate;
  const [mode, setMode] = useState<ImportMode>("create");
  const [file, setFile] = useState<File | null>(null);
  // Uma chave por arquivo escolhido: reenviar o mesmo (clique duplo, rede) não cria outra importação.
  const [key, setKey] = useState(() => crypto.randomUUID());
  const send = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("file", file!);
      return api<{ importId: string }>(`/imports/${p.entity}?mode=${upsertOk ? mode : "create"}`, {
        method: "POST",
        body: fd,
        headers: { "Idempotency-Key": key },
      });
    },
    onSuccess: (r) => {
      toast("info", "Planilha enviada. A prévia aparece assim que a validação terminar.");
      qc.invalidateQueries({ queryKey: ["imports", companyId, p.entity] });
      p.onCreated(r.importId);
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const template = () => downloadText(`modelo-${p.entity}.csv`, importTemplateCsv(p.entity, p.withCost));
  return (
    <div className="box card mb-3">
      <div className="card-body">
        <h2 className="h5">Nova importação de {IMPORT_ENTITY_LABEL[p.entity].toLowerCase()}</h2>
        <p className="text-muted small mb-3">
          Envie um arquivo CSV (separado por ponto e vírgula, vírgula ou tabulação) ou Excel (.xlsx, só a
          primeira aba), com até 10 MB e 10.000 linhas. A primeira linha deve ter os nomes das colunas;
          colunas desconhecidas são ignoradas. Valores em reais (1.234,56) e datas em dd/mm/aaaa. Nada é
          gravado antes da sua confirmação.{" "}
          <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={template}>
            Baixar modelo (CSV)
          </button>
        </p>
        <form
          className="row g-3 align-items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (file) send.mutate();
          }}
        >
          <div className="col-md-6">
            <label htmlFor="import-file" className="form-label">
              Planilha
            </label>
            <input
              id="import-file"
              type="file"
              className="form-control"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setKey(crypto.randomUUID());
              }}
            />
          </div>
          {upsertOk && (
            <fieldset className="col-md-4">
              <legend className="form-label fs-6 mb-1">Registros que já existem</legend>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  id="mode-create"
                  name="mode"
                  checked={mode === "create"}
                  onChange={() => setMode("create")}
                />
                <label className="form-check-label" htmlFor="mode-create">
                  Somente incluir novos
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  id="mode-upsert"
                  name="mode"
                  checked={mode === "upsert"}
                  onChange={() => setMode("upsert")}
                />
                <label className="form-check-label" htmlFor="mode-upsert">
                  Incluir novos e atualizar existentes (
                  {p.entity === "products" ? "pelo SKU" : "pelo CPF/CNPJ ou código"})
                </label>
              </div>
            </fieldset>
          )}
          <div className="col-md-2">
            <button type="submit" className="btn btn-primary w-100" disabled={!file || send.isPending}>
              Enviar planilha
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ImportsList(p: { entity: ImportEntity; selected?: string; onOpen(id: string): void }): ReactElement {
  const { companyId, api } = useSession();
  const q = useQuery({
    queryKey: ["imports", companyId, p.entity],
    queryFn: () => api<{ data: ImportView[] }>(`/imports/${p.entity}`),
    refetchInterval: (x) => (x.state.data?.data.some((i) => busy(i.status)) ? 2000 : false),
  });
  return (
    <div className="box card">
      <div className="card-body pb-0">
        <h2 className="h5">Importações recentes</h2>
      </div>
      <QueryStatus loading={q.isLoading} error={q.error} />
      {q.isSuccess && (
        <div className="table-responsive" tabIndex={0} role="region" aria-label="Importações recentes">
          <table className="table mb-0">
            <thead>
              <tr>
                <th scope="col">Arquivo</th>
                <th scope="col">Enviada em</th>
                <th scope="col">Situação</th>
                <th scope="col" className="text-end">
                  Linhas
                </th>
                <th scope="col">
                  <span className="visually-hidden">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {q.data.data.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    Nenhuma importação ainda.
                  </td>
                </tr>
              )}
              {q.data.data.map((i) => (
                <tr key={i.id} className={i.id === p.selected ? "table-active" : undefined}>
                  <td className="text-break">{i.fileName}</td>
                  <td>{dateTime(i.createdAt)}</td>
                  <td>
                    <StatusBadge tone={STATUS[i.status][0]}>{importStatusLabel(i)}</StatusBadge>
                  </td>
                  <td className="text-end">{n(i.counts.total)}</td>
                  <td className="text-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      aria-label={`Abrir importação ${i.fileName} de ${dateTime(i.createdAt)}`}
                      onClick={() => p.onOpen(i.id)}
                    >
                      Abrir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ImportDetail(p: {
  entity: ImportEntity;
  id: string;
  companyId: string | null;
  api: ReturnType<typeof useSession>["api"];
}): ReactElement {
  const { entity, id, api, companyId } = p;
  const qc = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState<(typeof ROW_FILTERS)[number][0]>("error");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [commitKey] = useState(() => crypto.randomUUID());
  const q = useQuery({
    queryKey: ["import", companyId, entity, id],
    queryFn: () => api<ImportView>(`/imports/${entity}/${id}`),
    refetchInterval: (x) => (x.state.data && busy(x.state.data.status) ? 2000 : false),
  });
  const v = q.data;
  const rows = useInfiniteQuery({
    queryKey: ["import-rows", companyId, entity, id, filter, v?.status],
    enabled: !!v && !busy(v.status),
    initialPageParam: -1,
    queryFn: ({ pageParam }) =>
      api<ImportRowsResult>(
        `/imports/${entity}/${id}/rows?status=${filter}&limit=50${pageParam >= 0 ? `&cursor=${pageParam}` : ""}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["import", companyId, entity, id] });
    qc.invalidateQueries({ queryKey: ["imports", companyId, entity] });
  };
  const commit = useMutation({
    mutationFn: () =>
      api(`/imports/${entity}/${id}/commit`, { method: "POST", headers: { "Idempotency-Key": commitKey } }),
    onSuccess: () => {
      toast("info", "Gravação iniciada. Acompanhe o andamento aqui.");
      refresh();
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const cancel = useMutation({
    mutationFn: () => api(`/imports/${entity}/${id}/cancel`, { method: "POST" }),
    onSuccess: () => {
      toast("success", "Importação cancelada.");
      refresh();
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const report = async (kind: "errors" | "result") => {
    try {
      const r = await api<{ url: string }>(`/imports/${entity}/${id}/report?kind=${kind}`, {
        method: "POST",
      });
      window.location.assign(r.url);
    } catch (e) {
      toast("danger", errorMessage(e));
    }
  };
  if (!v) return <QueryStatus loading={q.isLoading} error={q.error} />;
  const list = rows.data?.pages.flatMap((x) => x.data) ?? [];
  const { canCommit, resume, canCancel } = importActions(v);
  const c = v.counts;
  return (
    <section className="box card mb-3" aria-labelledby="import-detail-title">
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
          <div>
            <h2 className="h5 mb-1" id="import-detail-title">
              {v.fileName}
            </h2>
            <StatusBadge tone={STATUS[v.status][0]}>{importStatusLabel(v)}</StatusBadge>{" "}
            <span className="small text-muted">
              {v.mode === "upsert" ? "Incluir e atualizar" : "Somente incluir"} · enviada em{" "}
              {dateTime(v.createdAt)}
              {v.status === "validated" && !expired(v) && <> · confirme até {dateTime(v.expiresAt)}</>}
            </span>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-start">
            {v.hasErrorReport && (
              <button type="button" className="btn btn-outline-secondary" onClick={() => report("errors")}>
                Baixar relatório de erros
              </button>
            )}
            {v.hasResultReport && (
              <button type="button" className="btn btn-outline-secondary" onClick={() => report("result")}>
                Baixar resultado
              </button>
            )}
            {canCancel && (
              <button type="button" className="btn btn-outline-danger" onClick={() => setConfirmCancel(true)}>
                Cancelar importação
              </button>
            )}
            {(canCommit || resume) && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={commit.isPending}
                onClick={() => commit.mutate()}
              >
                {resume
                  ? "Retomar gravação"
                  : `Confirmar importação de ${n(c.valid)} ${c.valid === 1 ? "linha" : "linhas"}`}
              </button>
            )}
          </div>
        </div>
        {busy(v.status) && (
          <p className="text-muted" role="status">
            {v.status === "validating" ? "Validando a planilha…" : "Gravando os registros…"} A página atualiza
            sozinha.
          </p>
        )}
        {v.fileErrors.map((e) => (
          <div key={e} className="alert alert-danger py-2 mb-2" role="alert">
            {e}
          </div>
        ))}
        {v.warnings.map((w) => (
          <div key={w} className="alert alert-warning py-2 mb-2">
            {w}
          </div>
        ))}
        {!busy(v.status) && v.fileErrors.length === 0 && (
          <dl className="row small mb-2">
            <dt className="col-auto">Linhas lidas</dt>
            <dd className="col-auto">{n(c.total)}</dd>
            <dt className="col-auto">Válidas</dt>
            <dd className="col-auto">{n(c.valid)}</dd>
            <dt className="col-auto">Com erro</dt>
            <dd className="col-auto">{n(c.errors)}</dd>
            {(v.status === "done" || v.committedAt) && (
              <>
                <dt className="col-auto">Incluídas</dt>
                <dd className="col-auto">{n(c.created)}</dd>
                <dt className="col-auto">Atualizadas</dt>
                <dd className="col-auto">{n(c.updated)}</dd>
                <dt className="col-auto">Não gravadas</dt>
                <dd className="col-auto">{n(c.failed)}</dd>
              </>
            )}
          </dl>
        )}
        {v.columns.length > 0 && (
          <p className="small text-muted mb-2">Colunas reconhecidas: {v.columns.join(", ")}.</p>
        )}
      </div>
      {!busy(v.status) && v.fileErrors.length === 0 && (
        <>
          <div className="px-3">
            <div className="btn-group btn-group-sm mb-2" role="group" aria-label="Filtrar linhas">
              {ROW_FILTERS.map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  className={`btn ${filter === k ? "btn-secondary" : "btn-outline-secondary"}`}
                  aria-pressed={filter === k}
                  onClick={() => setFilter(k)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <QueryStatus loading={rows.isLoading} error={rows.error} />
          <div className="table-responsive" tabIndex={0} role="region" aria-label="Linhas da planilha">
            <table className="table table-sm mb-0">
              <caption className="visually-hidden">Linhas da planilha</caption>
              <thead>
                <tr>
                  <th scope="col">Linha</th>
                  <th scope="col">Identificação</th>
                  <th scope="col">Situação</th>
                  <th scope="col">Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {rows.isSuccess && list.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-3">
                      Nenhuma linha neste filtro.
                    </td>
                  </tr>
                )}
                {list.map((r) => (
                  <tr key={r.rowNo}>
                    <td>{r.rowNo}</td>
                    <td className="text-break">{r.key ?? "—"}</td>
                    <td>
                      <StatusBadge tone={ROW_STATUS[r.status]![0]}>
                        {r.status === "done" && r.outcome === "updated"
                          ? "Atualizada"
                          : r.status === "done"
                            ? "Incluída"
                            : ROW_STATUS[r.status]![1]}
                      </StatusBadge>
                    </td>
                    <td className="small">
                      {r.errors.length > 0 ? (
                        <ul className="mb-0 ps-3">
                          {r.errors.map((e) => (
                            <li key={`${e.column}-${e.message}`}>
                              <strong>{e.column}:</strong> {e.message}
                            </li>
                          ))}
                        </ul>
                      ) : r.values ? (
                        Object.entries(r.values)
                          .slice(0, 6)
                          .map(([k, val]) => `${k}: ${val}`)
                          .join(" · ")
                      ) : (
                        (r.message ?? "")
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.hasNextPage && (
            <div className="p-3">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={rows.isFetchingNextPage}
                onClick={() => rows.fetchNextPage()}
              >
                Carregar mais linhas
              </button>
            </div>
          )}
        </>
      )}
      <ConfirmDialog
        open={confirmCancel}
        title="Cancelar importação"
        message="A planilha é descartada e o que ainda não foi gravado não será importado. Continuar?"
        confirmLabel="Cancelar importação"
        cancelLabel="Voltar"
        onConfirm={() => {
          setConfirmCancel(false);
          cancel.mutate();
        }}
        onCancel={() => setConfirmCancel(false)}
      />
    </section>
  );
}
